import * as React from 'react';
import { observer } from 'mobx-react';
import { mobileDeviceFullStore } from '../store/store';
import MobileDeviceDetails from './MobileDeviceDetails';
import MobileDeviceLogging from './MobileDeviceLogging';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { CommandButton, IconButton, ButtonType } from 'office-ui-fabric-react/lib/Button';
import {
    DetailsList,
    IColumn,
    SelectionMode,
    DetailsListLayoutMode,
    ColumnActionsMode,
    CheckboxVisibility,
    IDetailsRowProps,
} from 'office-ui-fabric-react/lib/DetailsList';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { ISelection, Selection } from 'office-ui-fabric-react/lib/Selection';
import { toggleDeviceLoggingWindow } from '../mutators/toggleDeviceLoggingWindow';
import { toggleDeviceLoggingEnabled } from '../mutators/toggleDeviceLoggingEnabled';
import { markIsDevicesDirty } from '../mutators/markIsDevicesDirty';
import { markIsNotSendReadReceiptDirty } from '../mutators/markIsNotSendReadReceiptDirty';
import { markIsLoggingEnabledDirty } from '../mutators/markIsLoggingEnabledDirty';
import { updateNotSendReadReceipt } from '../mutators/updateNotSendReadReceipt';
import { selectMobileDevice } from '../mutators/selectMobileDevice';
import { deleteMobileDevice } from '../mutators/deleteMobileDevice';
import { initMobileDeviceFullOption } from '../orchestrators/initMobileDeviceFullOption';
import strings from 'owa-strings/owa-options-strings';
import { ControlIcons } from 'owa-control-icons';
import EditButton from './EditButton';

const styles = require('./MobileDeviceFull.scss');

@observer
export default class MobileDeviceFull extends React.Component<{}, {}> {
    private selection: ISelection;

    constructor(props: any) {
        super(props);
        this.selection = new Selection({
            onSelectionChanged: this.onSelectionChanged,
            getKey: this.getKey,
        });
    }

    private onSelectionChanged = () => {
        let selectedIndices = this.selection.getSelectedIndices();

        if (selectedIndices.length > 0) {
            selectMobileDevice(selectedIndices[0]);
        } else {
            let store = mobileDeviceFullStore();
            if (store.selected >= 0) {
                selectMobileDevice(-1);
            }
        }
    };

    private getKey = (item: any, index: number) => {
        return index;
    };

    private onDeviceLoggingClicked = () => {
        toggleDeviceLoggingEnabled();
        toggleDeviceLoggingWindow();
        markIsLoggingEnabledDirty(true);
    };

    private onSendReceiptChanged = (
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        updateNotSendReadReceipt(checked);
        markIsNotSendReadReceiptDirty(true);
    };

    private onRenderRow = (
        props?: IDetailsRowProps,
        defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null
    ): JSX.Element | null => {
        if (defaultRender) {
            return <div className={styles.row}>{defaultRender(props)}</div>;
        }

        return null;
    };

    private onRemoveDevice = () => {
        let store = mobileDeviceFullStore();
        if (store.selected >= 0) {
            deleteMobileDevice(store.selected);
            markIsDevicesDirty(true);
        }
    };

    private onGetOutlookAppClicked = () => {
        window.open('https://go.microsoft.com/fwlink/?linkid=870116');
    };

    private onRenderItemColumn = (item?: any, index?: number, column?: IColumn): any => {
        if (column.key === 'command') {
            return (
                <div className={styles.commandCol}>
                    <EditButton index={index} />
                    <IconButton
                        buttonType={ButtonType.command}
                        onClick={this.onRemoveDevice}
                        iconProps={{ iconName: ControlIcons.Delete }}
                        className={styles.command}
                        title={strings.options_mobile_full_deleteButton}
                    />
                    <IconButton
                        buttonType={ButtonType.command}
                        onClick={initMobileDeviceFullOption}
                        iconProps={{ iconName: ControlIcons.Refresh }}
                        className={styles.command}
                        title={strings.options_mobile_full_refreshButton}
                    />
                    {mobileDeviceFullStore().isLoggingEnabled ? (
                        <IconButton
                            buttonType={ButtonType.command}
                            onClick={this.onDeviceLoggingClicked}
                            iconProps={{ iconName: ControlIcons.PageRemove }}
                            className={styles.command}
                            title={strings.options_mobile_full_disableLoggingButton}
                        />
                    ) : (
                        <IconButton
                            buttonType={ButtonType.command}
                            onClick={this.onDeviceLoggingClicked}
                            iconProps={{ iconName: ControlIcons.PageEdit }}
                            className={styles.command}
                            title={strings.options_mobile_full_enableLoggingButton}
                        />
                    )}
                </div>
            );
        }

        return item[column.key];
    };

    private get columns(): IColumn[] {
        return [
            {
                key: 'deviceType',
                fieldName: 'deviceType',
                name: strings.options_mobile_full_deviceColumn,
                minWidth: 100,
                maxWidth: 150,
                isResizable: false,
                columnActionsMode: ColumnActionsMode.disabled,
                className: styles.detailsListColumn,
                headerClassName: styles.detailsListHeader,
            },
            {
                key: 'phoneNumber',
                fieldName: 'phoneNumber',
                name: strings.options_mobile_full_phoneNumberColumn,
                minWidth: 100,
                maxWidth: 150,
                isResizable: false,
                columnActionsMode: ColumnActionsMode.disabled,
                className: styles.detailsListColumn,
                headerClassName: styles.detailsListHeader,
            },
            {
                key: 'status',
                fieldName: 'status',
                name: strings.options_mobile_full_statusColumn,
                minWidth: 100,
                maxWidth: 150,
                isResizable: false,
                columnActionsMode: ColumnActionsMode.disabled,
                className: styles.detailsListColumn,
                headerClassName: styles.detailsListHeader,
            },
            {
                key: 'command',
                fieldName: 'Command',
                name: '',
                minWidth: 150,
                maxWidth: 250,
                isResizable: false,
                columnActionsMode: ColumnActionsMode.disabled,
                className: styles.detailsListColumn,
                headerClassName: styles.detailsListHeader,
            },
        ];
    }

    render() {
        let store = mobileDeviceFullStore();

        if (store.isDetailsOpen) {
            return (
                <MobileDeviceDetails
                    deviceDetails={store.selected >= 0 ? store.devices[store.selected] : null}
                />
            );
        } else if (store.isLoggingOpen) {
            return <MobileDeviceLogging />;
        } else {
            return (
                <div>
                    <div>
                        <Label className={styles.title}>{strings.options_mobile_full_title}</Label>
                    </div>
                    <DetailsList
                        className={styles.detailsList}
                        items={store.devices}
                        columns={this.columns}
                        selection={this.selection}
                        selectionMode={SelectionMode.single}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionPreservedOnEmptyClick={true}
                        checkboxVisibility={CheckboxVisibility.hidden}
                        onRenderRow={this.onRenderRow}
                        onRenderItemColumn={this.onRenderItemColumn}
                    />
                    <Checkbox
                        className={styles.readReceipt}
                        label={strings.options_mobile_full_readReceiptCheckbox}
                        checked={store.notSendReadReceipt}
                        onChange={this.onSendReceiptChanged}
                    />
                    <CommandButton
                        onClick={this.onGetOutlookAppClicked}
                        iconProps={{ iconName: ControlIcons.OutlookLogoInverse }}
                        className={styles.getOutlookAppLink}
                        text={strings.options_mobile_get_outlook_appButton}
                    />
                </div>
            );
        }
    }
}
